import { Suspense, useCallback, useEffect, useState, useRef } from 'react';
import {
    BrowserRouter as Router,
    useLocation,
    useNavigate,
    useRoutes,
} from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { PrismicProvider } from '@prismicio/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import Cookies from 'js-cookie';
import { client } from '../../../../utils/prismic/prismic';
import store, { persistor } from '../../../../redux/phioStore';
import { closeNotification } from '../../../../redux/notification/notificationActions';
import RouteLoading from '../RouteLoading/RouteLoading';
import SnakbarNotification from '../SnakbarNotification/SnakbarNotification';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { PhioStore } from '../../../../redux/types';
import { PhioProps, AppProps } from './types';
import theme from '../../../../themes/phioTheme';
import FbAnalytics from '../../../../services/analytics/FbAnalytics';
import CookiesOverlay from '../CookiesOverlay';
import EnvBanner from '../EnvBanner/EnvBanner';
import AppCheckProvider from '../../../../hooks/AppCheckProvider';
import { getAppCheck, misconfiguredUrl } from '../../../../services/firebase';
import { isLocalHost, isTestingEnv } from '../../../../utils/environmentUtil';

// Wrapper to initialize Turnstile widget
const Wrapper = (props: { children: JSX.Element }) => {
    const { children } = props;
    const [isRendered, setIsRendered] = useState(false);
    const initialisedTurnstile = useRef<boolean>(false);

    const useDummyTurnstile = isLocalHost() || isTestingEnv();
    useEffect(() => {
        if (initialisedTurnstile.current) {
            return;
        }
        initialisedTurnstile.current = true;
        // We can retrieve the turnstile ID returned by the render method below
        // Use that ID to retrieve the Turnstile response in firebase.ts
        window?.turnstile?.render('#myWidget', {
            sitekey: useDummyTurnstile
                ? '1x00000000000000000000BB'
                : '0x4AAAAAABklf6LfUSgVBBXJ',
            callback: () => {
                // fully rendered and callback triggered
                setIsRendered(true);
            },
            'error-callback': () => {
                console.error('Turnstile error callback triggered');
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return isRendered ? children : <RouteLoading />;
};

const Phio = (props: PhioProps) => {
    const { routes } = props;
    const dispatch = useDispatch();

    const [loadingup, setLoadingup] = useState(true);

    const notificationMessage = useSelector(
        (state: PhioStore) => state.notificationReducer.message,
    );
    const showNotification = useSelector(
        (state: PhioStore) => state.notificationReducer.show,
    );
    const notificationVariant = useSelector(
        (state: PhioStore) => state.notificationReducer.variant,
    );
    const notificationSx = useSelector(
        (state: PhioStore) => state.notificationReducer.sx,
    );
    const notificationAutoHide = useSelector(
        (state: PhioStore) => state.notificationReducer.autoHide,
    );
    const notificationAutoHideDuration = useSelector(
        (state: PhioStore) => state.notificationReducer.autoHideDuration,
    );

    const clientRoutes = useRoutes(routes);
    const navigate = useNavigate();

    const dispatchCloseNotification = useCallback(
        () => dispatch(closeNotification()),
        // eslint-disable-next-line
        [dispatch],
    );

    const cookieYes = Cookies.get('cookieyes-consent');
    const noAnaliticsConsent = cookieYes?.includes('analytics:no');

    const { hostname, pathname } = window.location;

    const showCookiesOverlay =
        hostname === 'phio.eql.ai' ||
        hostname === 'phio-playground.eql.ai' ||
        hostname === 'staging.eql.ai' ||
        hostname === 'test.eql.ai';

    useEffect(() => {
        const removeLogRocketScript = async () => {
            const script = document.head.querySelector(
                'script[src="https://cdn.lr-in-prod.com/logger-1.min.js"]',
            );

            if (script && noAnaliticsConsent) {
                await document.head.removeChild(script);
                Cookies.remove('_lr_tabs_-kxe71y/phio', {
                    path: '/',
                });
                Cookies.remove('_lr_hb_-kxe71y/phio', {
                    path: '/',
                });
                Cookies.remove('_lr_uf_-kxe71y', {
                    path: '/',
                });
            }
        };
        removeLogRocketScript();
    }, [noAnaliticsConsent]);

    useEffect(() => {
        FbAnalytics.setUserProperty({ application: 'access' });
    }, []);

    const { state } = useLocation();

    useEffect(() => {
        let isMounted = true;
        (async () => {
            if (!isMounted) {
                return;
            }
            const segments = pathname.split('/');
            const path = segments[segments.length - 1];
            const res = (await misconfiguredUrl(path)) as {
                data: { url_path: string };
            };

            if (res.data.url_path) {
                FbAnalytics.logEvent('misconfigured_url', {
                    redirected_at: new Date(),
                    url: pathname,
                });
                navigate(res.data.url_path, {
                    state: { redirected: state?.redirected },
                });
            }
            setLoadingup(false);
        })();
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    return (
        <AppCheckProvider appCheck={getAppCheck()}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <PrismicProvider client={client}>
                    <Suspense fallback={<RouteLoading />}>
                        {loadingup ? <RouteLoading /> : clientRoutes}
                    </Suspense>
                </PrismicProvider>
                {showCookiesOverlay && <CookiesOverlay />}
                <SnakbarNotification
                    sx={notificationSx}
                    show={showNotification}
                    message={notificationMessage}
                    variant={notificationVariant}
                    handleClose={dispatchCloseNotification}
                    autoHideDuration={notificationAutoHideDuration}
                    autoHide={notificationAutoHide}
                />
                <EnvBanner />
            </ThemeProvider>
        </AppCheckProvider>
    );
};

const App = (props: AppProps) => {
    const { routes } = props;

    return (
        <Router>
            <Provider store={store}>
                <PersistGate loading={<div>LOADING</div>} persistor={persistor}>
                    <ErrorBoundary>
                        <Wrapper>
                            <Phio routes={routes} />
                        </Wrapper>
                    </ErrorBoundary>
                </PersistGate>
            </Provider>
        </Router>
    );
};

export default App;
